import { forwardRef, useEffect, useState } from "react";
import PropTypes from "prop-types";

import LiveTapInfoPanel from "./LiveTapInfoPanel";
import SplitStage from "./SplitStage";
import useSymbolName from "../hooks/useSymbolName";
import { TapestryCanvas } from "../vendor/liveTapRenderer";

/**
 * The classic live system's stage: the curated tapestry canvas beside the
 * symbol-description + transcript panel (TT-972). Shared by the host's Live
 * Tap page and the public viewer (TT-1110) so the two can never drift apart;
 * the host passes its own extras (export ref, background pin, debug tools)
 * through `canvasProps`.
 *
 * `infoOpen` is the caller's — its toolbar toggles it — while everything the
 * panel needs to fill itself (which background is painted, its symbol's
 * description) lives here; the row itself, and the panel's move to the
 * fullscreen gutter, is SplitStage, shared with the scene system.
 */
const ClassicLiveStage = forwardRef(function ClassicLiveStage(
  { segments, currentSent, threshold, meetingName, meetingDate, resources, infoOpen, canvasProps },
  ref
) {
  // The renderer tells us which background it's painting (`{ id,
  // primarySymbolId }`) so the panel can name the symbol and fetch its
  // description.
  const [activeBackground, setActiveBackground] = useState(null);
  const [symbolDescription, setSymbolDescription] = useState("");
  const nameOf = useSymbolName(resources);
  const primarySymbolId = activeBackground?.primarySymbolId;

  // Field-guide description of the painted background's primary symbol. Refetched
  // whenever the renderer swaps backgrounds; cleared while generative. Gated on
  // `infoOpen` so a session that never opens the panel never asks: the renderer
  // re-picks backgrounds throughout a meeting, and every swap would otherwise cost
  // a request for text nobody is looking at.
  useEffect(() => {
    const bgId = activeBackground?.id;
    if (!infoOpen || !bgId) {
      setSymbolDescription("");
      return undefined;
    }
    let cancelled = false;
    resources
      .loadBackgroundDescription(bgId)
      .then((desc) => {
        if (!cancelled) setSymbolDescription(desc);
      })
      .catch(() => {
        if (!cancelled) setSymbolDescription("");
      });
    return () => {
      cancelled = true;
    };
  }, [infoOpen, activeBackground?.id, resources]);

  const currentTurn = canvasProps?.currentTurn;
  const infoPanel = (
    <LiveTapInfoPanel
      symbolName={primarySymbolId ? nameOf(primarySymbolId) : ""}
      description={symbolDescription}
      segments={segments}
      currentTurn={currentTurn}
    />
  );
  const { currentTurn: _omit, ...canvasExtras } = canvasProps || {};

  return (
    <SplitStage infoOpen={infoOpen} panel={infoPanel}>
      {({ aside, onFullscreenChange }) => (
        <TapestryCanvas
          ref={ref}
          segments={segments}
          currentSent={currentSent}
          threshold={threshold}
          meetingName={meetingName}
          meetingDate={meetingDate}
          resources={resources}
          onActiveBackgroundChange={setActiveBackground}
          fullscreenAside={aside}
          onFullscreenChange={onFullscreenChange}
          {...canvasExtras}
        />
      )}
    </SplitStage>
  );
});

ClassicLiveStage.propTypes = {
  segments: PropTypes.array.isRequired,
  currentSent: PropTypes.number.isRequired,
  threshold: PropTypes.number,
  meetingName: PropTypes.string,
  meetingDate: PropTypes.string,
  resources: PropTypes.object.isRequired,
  infoOpen: PropTypes.bool,
  // Host-only extras forwarded to the canvas (debugTools, the background pin
  // and its callbacks); `currentTurn` is lifted out for the transcript panel.
  canvasProps: PropTypes.object,
};

export default ClassicLiveStage;
